import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useEventLocationContext } from "@stadium/services/analytics/context/event-location";
import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import { withSyncFeatureFlag } from "@stadium/services/launch-darkly";

import { useStoreContext } from "@components/store/provider";
import { useCheckoutContext } from "@contexts/checkout-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import {
  getCartOrderFetched,
  getCartOrderFetching,
  getCartOrderId,
  getCartOrderNumber,
  getCartItemsCount,
  getCartOrderBagTotal,
} from "@selectors/cart";
import { getCountry } from "@selectors/country";
import { getIsPricelessShop } from "@selectors/store";

import useRefreshCumulativeCart from "@hooks/cart/use-refresh-cumulative-cart";

import CheckAuthWithPreviewMode from "@components/hoc/check-auth-with-preview-mode";
import { InternalButton } from "@components/shared/buttons";
import CheckoutSteps from "@components/store/checkout/checkout-steps";
import CheckoutSummary from "@components/store/checkout/checkout-summary";
import Portal from "@stadium/ui/src/components/portal";

import { PREVIEW_MODE_DEFAULT_ORDER_ID } from "@constants/preview-mode";
import { getCartRoute, getCheckoutRoute, getStoreHomeRoute } from "@constants/routes";

import main from "./main.module.scss";
import { PageLoader } from "@stadium/ui/src/components/loaders/loaders";

const Checkout = ({ cmsSectionIndex = 0 }) => {
  const router = useRouter();
  const { query } = router;
  const { storeBasePath, storeNumber } = useStoreContext();
  const { previewMode } = usePreviewModeContext();

  const [isMounted, setMounted] = useState(false);
  const { id: countryId } = useSelector(getCountry);
  const orderNumber = useSelector(getCartOrderNumber);
  const orderNumberFetching = useSelector(getCartOrderFetching);
  const orderId = useSelector(getCartOrderId);
  const orderFetched = useSelector(getCartOrderFetched);
  const isPricelessShop = useSelector(getIsPricelessShop);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const { refreshCart } = useRefreshCumulativeCart();

  const { submittedAddress, setSubmittedAddress } = useCheckoutContext();

  const { location, services, baseEventLocationProperties } = useEventLocationContext();
  const { trackPage } = useActionEvents(services);
  const itemsCount = useSelector(getCartItemsCount);
  const cartValue = useSelector(getCartOrderBagTotal);

  const checkoutPageViewTrackedRef = useRef(false);

  useEffect(() => {
    if (!orderFetched || checkoutPageViewTrackedRef.current) {
      return;
    }

    checkoutPageViewTrackedRef.current = true;
    trackPage({
      page: location,
      shop_number: storeNumber,
      cart_value: cartValue,
      item_count: itemsCount,
      ...baseEventLocationProperties,
    });
    // Page view should fire once per checkout load, not on every cart refresh (cartValue/itemsCount).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional snapshot at first ready state
  }, [orderFetched, location, storeNumber, trackPage, baseEventLocationProperties]);

  //redirect to cart page if user changes country
  //not including isMounted in the dependency array because we want the
  //else part to run only when countryId updates for the second time
  // not including the router dependency because it calls useEffect again if we change order number in guest user flow
  useEffect(() => {
    if (countryId) {
      if (!isMounted) setMounted(true);
      else router.push(getCartRoute({ storeBasePath }));
    }
  }, [countryId, storeBasePath]);

  useEffect(() => {
    // this shouldn't be called on refresh page since we are calling refreshCart from navigation as well on refresh.
    if (orderFetched) {
      refreshCart(orderId);
    }
  }, []);

  //redirect to home route incase of invalid orderNumber
  // update the orderNumber incase of user was in guestmode and login
  useEffect(() => {
    if (!orderNumberFetching && !previewMode) {
      const { slug, orderNumber: urlOrderNumber } = query;
      const finalOrderNumber = urlOrderNumber || slug;
      if (finalOrderNumber === PREVIEW_MODE_DEFAULT_ORDER_ID) {
        router.replace(getCheckoutRoute({ storeBasePath, orderNumber: finalOrderNumber }));
        return;
      }
      if (orderNumber !== finalOrderNumber) router.push(getStoreHomeRoute({ storeBasePath }));
    }
  }, [orderNumber, orderNumberFetching]);

  const renderBackCta = () => {
    const handleGoBack = () => {
      if (submittedAddress?.externalId) setSubmittedAddress({});
      else router.push(getCartRoute({ storeBasePath }));
    };
    return (
      <InternalButton styleType="link" onClick={handleGoBack} className={main.backLink}>
        {" "}
        <i className={main.iconArrowLeft} />
        <span>Back</span>
      </InternalButton>
    );
  };

  const renderLeftScreen = () => <CheckoutSteps />;

  const renderCheckoutBody = () => {
    return (
      <div className={main.rowBlockgroup}>
        <div className={`${main.leftBlock} ${cmsSectionIndex === 0 ? "" : main.cmsSectionIndex}`}>
          {renderLeftScreen()}
        </div>
        <div className={`${main.rightBlock} ${cmsSectionIndex === 0 ? "" : main.cmsSectionIndex}`}>
          <CheckoutSummary />
        </div>
      </div>
    );
  };

  const renderBackBtnSection = () => (
    <Portal id="checkout-back-bar-container" dataContainerId="checkout-back-bar-portal">
      <div className={main.backBar}>{renderBackCta()}</div>
    </Portal>
  );

  return (
    <div
      className={`${main.checkoutContainer} ${isPricelessShop && main.isPricelessShop} ${
        isMobileDevice ? main.withCheckout : ""
      }`}
    >
      {!isMobileDevice && renderBackBtnSection()}
      {renderCheckoutBody()}
    </div>
  );
};

export default withSyncFeatureFlag(CheckAuthWithPreviewMode(Checkout), {
  loader: <PageLoader wrapperClass={main.loaderWrapper} />,
});
